import type { CSSProperties } from 'react';
import {
  BookA,
  Check,
  Eye,
  FileText,
  Headphones,
  type LucideIcon,
  MessageCircleQuestionMark,
  MessagesSquare,
  Music,
} from 'lucide-react';

import { formatDate } from '../i18n/format';
import { t } from '../i18n/t';
import { SET_DAYS, setProgress, type StripDay } from '../lessons/progress.ts';

const ICONS: Record<string, LucideIcon> = {
  ARTICLE: FileText,
  VOCABULARY: BookA,
  MINI_STORY: MessageCircleQuestionMark,
  POV: Eye,
  AUDIO_ARTICLE: Headphones,
  COMMENTARY: MessagesSquare,
};

export function KindTile({ kind, done = false, size = 'md' }: { kind: string; done?: boolean; size?: 'sm' | 'md' }) {
  const Icon = ICONS[kind] ?? Music;
  const box = size === 'sm' ? 'size-7 rounded-lg' : 'size-10 rounded-xl';
  const glyph = size === 'sm' ? 'size-4' : 'size-5';
  return (
    <span
      aria-hidden
      className={`relative grid shrink-0 place-items-center ${box} ${
        done ? 'bg-primary text-primary-content' : 'bg-primary/10 text-primary'
      }`}
    >
      <Icon className={glyph} />
      {done && size === 'md' && (
        <span className="bg-base-100 text-primary absolute -end-1 -bottom-1 grid size-4 place-items-center rounded-full shadow-sm">
          <Check className="size-3" strokeWidth={3} />
        </span>
      )}
    </span>
  );
}

/** daisyUI's `radial-progress`, driven by custom properties React's style type does not know. */
export function SetRing({ daysPlayed, size = '2.75rem' }: { daysPlayed: number; size?: string }) {
  const { fraction, finished } = setProgress(daysPlayed);
  const label = finished
    ? t('lessons-set-finished')
    : t('lessons-set-days', { days: Math.min(daysPlayed, SET_DAYS), total: SET_DAYS });
  return (
    <div
      role="img"
      aria-label={label}
      className={`radial-progress shrink-0 text-xs font-semibold ${
        finished ? 'bg-primary text-primary-content' : 'text-primary bg-base-200'
      }`}
      style={
        {
          '--value': Math.round(fraction * 100),
          '--size': size,
          '--thickness': '0.25rem',
        } as CSSProperties
      }
    >
      {finished ? (
        <Check aria-hidden className="size-5" strokeWidth={3} />
      ) : (
        <span aria-hidden className="text-base-content">
          {Math.min(daysPlayed, SET_DAYS)}
        </span>
      )}
    </div>
  );
}

export function DaySegments({ day, playedToday }: { day: number; playedToday: boolean }) {
  return (
    <div className="flex gap-1" aria-hidden>
      {Array.from({ length: SET_DAYS }, (_, i) => {
        const past = i < day - 1;
        const today = i === day - 1;
        return (
          <span
            key={i}
            className={`h-2 flex-1 rounded-full ${
              past || (today && playedToday)
                ? 'bg-primary'
                : today
                  ? 'bg-primary/30 ring-primary ring-1'
                  : 'bg-base-300'
            }`}
          />
        );
      })}
    </div>
  );
}

function asDate(localDay: string): Date {
  const [y, m, d] = localDay.split('-').map(Number);
  return new Date(y as number, (m as number) - 1, d);
}

/**
 * The last seven days as dots under their weekday: solid for a day with a
 * lesson heard through, pale for one only started. A tally rather than a
 * streak, so a missed day costs nothing but its own dot.
 */
export function WeekStrip({ days }: { days: readonly StripDay[] }) {
  const listened = days.filter((d) => d.state !== 'none').length;
  return (
    <section className="bg-base-100 border-base-200 flex flex-col gap-3 rounded-box border p-4">
      <div className="flex flex-col">
        <h2 className="font-semibold">{t('lessons-week-title')}</h2>
        <span className="text-base-content/60 text-sm">{t('lessons-week-summary', { count: listened })}</span>
      </div>
      <ol className="grid grid-cols-7 gap-1">
        {days.map((d) => {
          const date = asDate(d.localDay);
          const long = formatDate(date, { weekday: 'long', day: 'numeric', month: 'short' });
          const label =
            d.state === 'done'
              ? t('lessons-week-day-done', { date: long })
              : d.state === 'started'
                ? t('lessons-week-day-started', { date: long })
                : t('lessons-week-day-none', { date: long });
          return (
            <li key={d.localDay} className="flex flex-col items-center gap-1" aria-label={label}>
              <span aria-hidden className={`text-xs ${d.isToday ? 'text-primary font-bold' : 'text-base-content/60'}`}>
                {formatDate(date, { weekday: 'narrow' })}
              </span>
              <span
                aria-hidden
                className={`grid size-8 place-items-center rounded-full ${
                  d.state === 'done'
                    ? 'bg-primary text-primary-content'
                    : d.state === 'started'
                      ? 'bg-primary/25'
                      : 'bg-base-200'
                } ${d.isToday ? 'ring-primary ring-offset-base-100 ring-2 ring-offset-2' : ''}`}
              >
                {d.state === 'done' && <Check className="size-4" strokeWidth={3} />}
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
